import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Create permissions context
export const PermissionsContext = createContext({
  can_edit_schedules: false,
  can_create_reports: false,
  can_receive_alerts: false,
  can_manage_maintenance: false,
  can_manage_admins: false,
  can_manage_email_digests: false
});

// Hook to use permissions in child components
export const usePermissions = () => useContext(PermissionsContext);
import { 
  LayoutDashboard, MapPin, Calendar, LogOut, Sun, Moon, 
  ChevronLeft, ChevronRight, Menu, X, Wrench, ChevronDown, User,
  AlertTriangle, Clock, FileText, Bell, BellOff, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // Maintenance state
  const [maintenanceMode, setMaintenanceMode] = useState({ enabled: false, reason: '', estimated_end: '' });
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [maintenanceReason, setMaintenanceReason] = useState('');
  const [maintenanceEnd, setMaintenanceEnd] = useState('');
  
  // Email alerts state
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(false);
  const [savingEmailAlerts, setSavingEmailAlerts] = useState(false);
  
  // User permissions
  const [userPermissions, setUserPermissions] = useState({
    can_edit_schedules: false,
    can_create_reports: false,
    can_receive_alerts: false,
    can_manage_maintenance: false,
    can_manage_admins: false,
    can_manage_email_digests: false
  });
  useEffect(() => {
    // Charger la préférence de thème depuis localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
    } else {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
    
    // Charger la préférence de sidebar depuis localStorage
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsed === 'true') {
      setCollapsed(true);
    }
    
    // Charger les données utilisateur depuis le cache d'abord
    const cachedUserData = localStorage.getItem('userData');
    if (cachedUserData) {
      try {
        setUserData(JSON.parse(cachedUserData));
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Load cached permissions
    const cachedPerms = localStorage.getItem('userPermissions');
    if (cachedPerms) {
      try {
        setUserPermissions(JSON.parse(cachedPerms));
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);
  
  // Separate useEffect to fetch data after component mounts
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchUserDataAsync(),
        fetchMaintenanceStatusAsync(),
        fetchUserPermissionsAsync(),
        fetchEmailAlertsAsync()
      ]);
    };
    loadData();
  }, []);
  
  const fetchUserPermissionsAsync = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/admins/my-permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.permissions) {
          setUserPermissions(response.data.permissions);
          localStorage.setItem('userPermissions', JSON.stringify(response.data.permissions));
        }
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };
  
  const fetchUserDataAsync = async () => {
    try {
      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username');
      if (token && username) {
        const response = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserData(response.data);
        // Mettre en cache pour les prochains chargements
        localStorage.setItem('userData', JSON.stringify(response.data));
      }
    } catch (error) {
      // Si l'API échoue, utiliser le cache ou le username
      const cachedUserData = localStorage.getItem('userData');
      if (cachedUserData) {
        try {
          setUserData(JSON.parse(cachedUserData));
        } catch (e) {
          // Fallback au username
          const username = localStorage.getItem('username');
          if (username) {
            setUserData({ display_name: username, photo: null });
          }
        }
      } else {
        const username = localStorage.getItem('username');
        if (username) {
          setUserData({ display_name: username, photo: null });
        }
      }
    }
  };
  
  const fetchMaintenanceStatusAsync = async () => {
    try {
      const response = await axios.get(`${API}/maintenance`);
      setMaintenanceMode(response.data);
    } catch (error) {
      console.error('Erreur chargement maintenance:', error);
    }
  };
  
  const fetchEmailAlertsAsync = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmailAlertsEnabled(response.data.email_alerts_enabled || false);
      }
    } catch (error) {
      console.error('Error loading email alerts:', error);
    }
  };
  
  const toggleEmailAlerts = async () => {
    try {
      setSavingEmailAlerts(true);
      const token = localStorage.getItem('token');
      const newValue = !emailAlertsEnabled;
      await axios.put(`${API}/settings`, { email_alerts_enabled: newValue }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailAlertsEnabled(newValue);
      toast.success(newValue ? 'Notifications email activées' : 'Notifications email désactivées');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingEmailAlerts(false);
    }
  };
  
  const activateMaintenance = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/maintenance`, {
        enabled: true,
        reason: maintenanceReason,
        estimated_end: maintenanceEnd || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaintenanceMode({ enabled: true, reason: maintenanceReason, estimated_end: maintenanceEnd });
      setShowMaintenanceModal(false);
      setMaintenanceReason('');
      setMaintenanceEnd('');
      toast.success('Mode maintenance activé');
    } catch (error) {
      toast.error('Erreur lors de l\'activation');
    }
  };
  
  const deactivateMaintenance = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/maintenance`, {
        enabled: false,
        reason: null,
        estimated_end: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaintenanceMode({ enabled: false, reason: '', estimated_end: '' });
      setShowDisableModal(false);
      toast.success('Mode maintenance désactivé');
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    }
  };
  
  const toggleTheme = () => {
    const newMode = !darkMode;
    // Update localStorage FIRST before any state changes
    if (newMode) {
      localStorage.setItem('theme', 'dark');
    } else {
      localStorage.setItem('theme', 'light');
    }
    // Small delay to ensure localStorage is persisted before reload
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };
  
  const toggleSidebar = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    localStorage.setItem('sidebarCollapsed', newCollapsed.toString());
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('userData');
    toast.success('À bientôt');
    navigate('/');
  };
  
  // Menu items filtered by permissions
  const allMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', always: true },
    { icon: MapPin, label: 'Secteurs', path: '/admin/secteurs', always: true },
    { icon: Calendar, label: 'Planning', path: '/admin/plannings', always: true },
    { icon: FileText, label: 'Compte rendu', path: '/admin/compte-rendu', permission: 'can_create_reports' },
    { icon: Mail, label: 'Récaps e-mail', path: '/admin/email-recaps', permission: 'can_manage_email_digests' }
  ];
  
  const menuItems = allMenuItems.filter(item => 
    item.always || userPermissions[item.permission]
  );
  
  // Styles dynamiques
  const sidebarBg = darkMode ? '#1f2937' : '#ffffff';
  const sidebarBorder = darkMode ? '#374151' : '#e5e7eb';
  const mainBg = darkMode ? '#141c2b' : '#f8f9fa';
  const textPrimary = darkMode ? '#ffffff' : '#111827';
  const textSecondary = darkMode ? '#9ca3af' : '#6b7280';
  const hoverBg = darkMode ? '#374151' : '#f3f4f6';
  
  const SidebarContent = ({ isMobile = false }) => (
    <div className="h-full flex flex-col" style={{ background: sidebarBg }}>
      {/* Zone A - Identité */}
      <div 
        className={`flex items-center justify-center ${collapsed && !isMobile ? 'p-4' : 'p-6'}`}
        style={{ borderBottom: `1px solid ${sidebarBorder}` }}
      >
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_site-monitor-24/artifacts/kucp102x_AVATAR%20PNG.png" 
            alt="Logo" 
            className={`${collapsed && !isMobile ? 'h-10 w-10' : 'h-12 w-12'} object-contain`}
          />
          {(!collapsed || isMobile) && (
            <h2 
              className="text-xl font-bold whitespace-nowrap" 
              style={{ fontFamily: 'Outfit, sans-serif', color: textPrimary }}
            >
              Open Close
            </h2>
          )}
        </div>
      </div>
      
      {/* Zone B - Contexte Utilisateur */}
      <div 
        className={`relative ${collapsed && !isMobile ? 'px-2 py-3' : 'p-4'}`}
        style={{ borderBottom: `1px solid ${sidebarBorder}` }}
      >
        <button
          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
          className={`w-full flex items-center gap-3 rounded-lg transition-all ${
            collapsed && !isMobile ? 'justify-center p-2' : 'p-3'
          }`}
          style={{ 
            background: userDropdownOpen ? hoverBg : 'transparent',
            border: `1px solid ${sidebarBorder}`
          }}
        >
          {/* Photo utilisateur avec pastille en ligne */}
          <div className="relative flex-shrink-0">
            {userData?.photo ? (
              <img 
                src={userData.photo} 
                alt="Avatar" 
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#f76f4b] flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {(userData?.display_name || localStorage.getItem('username') || 'U')
                    .split(' ')
                    .map(n => n[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </span>
              </div>
            )}
            {/* Pastille verte "en ligne" */}
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
          </div>
          
          {(!collapsed || isMobile) && (
            <>
              <div className="flex-1 text-left min-w-0">
                <p 
                  className="font-semibold text-sm truncate" 
                  style={{ color: textPrimary }}
                >
                  {userData?.display_name || localStorage.getItem('username') || 'Utilisateur'}
                </p>
                <p 
                  className="text-xs truncate" 
                  style={{ color: textSecondary }}
                >
                  {userData?.position || 'Utilisateur'}
                </p>
              </div>
              <ChevronDown 
                className={`w-4 h-4 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`}
                style={{ color: textSecondary }}
              />
            </>
          )}
        </button>
        
        {/* Dropdown Overlay to close on outside click */}
        {userDropdownOpen && (
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setUserDropdownOpen(false)}
          />
        )}
        
        {/* Dropdown Menu */}
        {userDropdownOpen && (
          <div 
            className={`absolute z-50 mt-2 rounded-lg shadow-lg overflow-hidden ${
              collapsed && !isMobile ? 'left-full ml-2 top-0' : 'left-4 right-4'
            }`}
            style={{ 
              background: sidebarBg,
              border: `1px solid ${sidebarBorder}`,
              minWidth: collapsed && !isMobile ? '280px' : 'auto'
            }}
          >
            {/* 1. TOGGLE MODE SOMBRE/CLAIR - Toujours en premier */}
            <div 
              className="m-2 p-3 rounded-lg cursor-pointer transition-colors"
              style={{ background: darkMode ? '#374151' : '#f3f4f6' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleTheme();
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!darkMode ? (
                    <Sun size={18} className="text-yellow-500" />
                  ) : (
                    <Moon size={18} className="text-blue-400" />
                  )}
                  <span className="text-sm font-medium" style={{ color: textPrimary }}>
                    {!darkMode ? 'Mode clair' : 'Mode sombre'}
                  </span>
                </div>
                {/* Toggle Switch */}
                <div
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                    darkMode ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      darkMode ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
            </div>
            
            <div style={{ borderTop: `1px solid ${sidebarBorder}`, margin: '0 8px' }} />
            
            {/* 2. BOUTONS EXISTANTS */}
            {/* Maintenance button - only visible if user has can_manage_maintenance permission */}
            {userPermissions.can_manage_maintenance && (
              <button
                onClick={() => { 
                  setUserDropdownOpen(false);
                  if (maintenanceMode.enabled) {
                    setShowDisableModal(true);
                  } else {
                    setShowMaintenanceModal(true);
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer rounded-lg mx-2 my-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                style={{ color: maintenanceMode.enabled ? '#f59e0b' : textPrimary, width: 'calc(100% - 16px)' }}
              >
                <Wrench className="w-4 h-4" style={{ color: maintenanceMode.enabled ? '#f59e0b' : textSecondary }} />
                <span className="text-sm">{maintenanceMode.enabled ? 'Désactiver la maintenance' : 'Mode Maintenance'}</span>
              </button>
            )}
            
            {/* Email alerts toggle - only visible if user has can_receive_alerts permission */}
            {userPermissions.can_receive_alerts && (
              <button
                onClick={() => {
                  if (!savingEmailAlerts) {
                    toggleEmailAlerts();
                  }
                }}
                disabled={savingEmailAlerts}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer rounded-lg mx-2 my-1 hover:bg-gray-100 dark:hover:bg-gray-700 ${savingEmailAlerts ? 'opacity-50' : ''}`}
                style={{ color: emailAlertsEnabled ? '#22c55e' : textPrimary, width: 'calc(100% - 16px)' }}
              >
                {emailAlertsEnabled ? (
                  <>
                    <Bell className="w-4 h-4" style={{ color: '#22c55e' }} />
                    <span className="text-sm">Notifications activées</span>
                  </>
                ) : (
                  <>
                    <BellOff className="w-4 h-4" style={{ color: textSecondary }} />
                    <span className="text-sm">Notifications désactivées</span>
                  </>
                )}
              </button>
            )}
            
            <div style={{ borderTop: `1px solid ${sidebarBorder}`, margin: '8px' }} />
            
            {/* 3. DÉCONNEXION - Toujours en dernier */}
            <button
              onClick={() => handleLogout()}
              className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer rounded-lg mx-2 mb-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              style={{ width: 'calc(100% - 16px)' }}
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Déconnexion</span>
            </button>
          </div>
        )}
      </div>
      
      {/* Zone C - Navigation Principale */}
      <nav className={`flex-1 overflow-y-auto ${collapsed && !isMobile ? 'p-2' : 'p-4'} space-y-2`}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 rounded-lg text-left font-medium transition-all ${
                collapsed && !isMobile ? 'justify-center p-3' : 'px-4 py-3'
              } ${
                isActive 
                  ? 'bg-[#5BA4CF]/20 text-[#5BA4CF] border border-[#5BA4CF]/30' 
                  : ''
              }`}
              style={!isActive ? { color: textSecondary } : {}}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.background = hoverBg;
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.background = 'transparent';
              }}
              title={collapsed && !isMobile ? item.label : undefined}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {(!collapsed || isMobile) && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>
      
      {/* Zone D - Pied de Sidebar */}
      <div 
        className={`${collapsed && !isMobile ? 'p-2' : 'p-4'}`}
        style={{ borderTop: `1px solid ${sidebarBorder}` }}
      >
        <button
          onClick={toggleSidebar}
          className={`w-full flex items-center gap-3 rounded-lg transition-all ${
            collapsed && !isMobile ? 'justify-center p-3' : 'px-4 py-3'
          }`}
          style={{ color: textSecondary }}
          onMouseEnter={(e) => e.currentTarget.style.background = hoverBg}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          {collapsed && !isMobile ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5" />
              <span className="text-sm">Réduire</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen flex" style={{ background: mainBg }}>
      {/* Sidebar Desktop */}
      <aside 
        className={`hidden md:flex flex-col fixed h-screen z-40 transition-all duration-300 shadow-lg ${
          collapsed ? 'w-20' : 'w-64'
        }`}
        style={{ 
          background: sidebarBg,
          borderRight: `1px solid ${sidebarBorder}`
        }}
      >
        <SidebarContent />
      </aside>
      
      {/* Spacer pour le contenu principal */}
      <div className={`hidden md:block flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`} />
      
      {/* Mobile Header */}
      <div 
        className="md:hidden fixed top-0 left-0 right-0 h-16 z-50 flex items-center justify-between px-4 shadow-md"
        style={{ background: sidebarBg, borderBottom: `1px solid ${sidebarBorder}` }}
      >
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_8273b7de-0616-4daf-8063-852b843c4c40/artifacts/w2af3asz_logo-final-couleur.png" 
            alt="Logo" 
            className="h-8"
          />
          <span className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: textPrimary }}>
            My Open Close
          </span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg transition-all"
          style={{ color: textPrimary }}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Mobile Sidebar */}
      <aside 
        className={`md:hidden fixed top-16 left-0 bottom-0 w-64 z-50 transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: sidebarBg }}
      >
        <SidebarContent isMobile={true} />
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-y-auto md:pt-0 pt-16 min-h-screen">
        {/* Bandeau Maintenance */}
        {maintenanceMode.enabled && (
          <div 
            className="flex items-center justify-center gap-2 px-4 py-3 text-white font-medium"
            style={{ background: '#f59e0b' }}
          >
            <AlertTriangle className="h-5 w-5" />
            <span>Mode maintenance actif - Seuls les administrateurs ont accès à la plateforme</span>
          </div>
        )}
        <PermissionsContext.Provider value={userPermissions}>
          {children}
        </PermissionsContext.Provider>
      </main>
      
      {/* Modal Activer Maintenance */}
      {showMaintenanceModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div 
            className="w-full max-w-md mx-4 rounded-xl p-6 shadow-xl"
            style={{ background: sidebarBg }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg" style={{ background: '#f59e0b20' }}>
                <Wrench className="h-6 w-6" style={{ color: '#f59e0b' }} />
              </div>
              <h2 className="text-xl font-bold" style={{ color: textPrimary }}>Mode Maintenance</h2>
            </div>
            
            <div 
              className="p-4 rounded-lg mb-4 border"
              style={{ background: '#f59e0b10', borderColor: '#f59e0b30' }}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />
                <p className="text-sm" style={{ color: textSecondary }}>
                  Activer le mode maintenance bloquera toutes les connexions et actions pour les clients et gestionnaires. 
                  Seuls les administrateurs pourront continuer à utiliser le site.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                  Raison de la maintenance *
                </label>
                <textarea
                  value={maintenanceReason}
                  onChange={(e) => setMaintenanceReason(e.target.value)}
                  placeholder="Ex: Mise à jour du système..."
                  className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                  style={{ 
                    background: darkMode ? '#374151' : '#f9fafb',
                    borderColor: sidebarBorder,
                    color: textPrimary
                  }}
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Fin estimée (optionnel)
                  </div>
                </label>
                <input
                  type="datetime-local"
                  value={maintenanceEnd}
                  onChange={(e) => setMaintenanceEnd(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ 
                    background: darkMode ? '#374151' : '#f9fafb',
                    borderColor: sidebarBorder,
                    color: textPrimary
                  }}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowMaintenanceModal(false);
                  setMaintenanceReason('');
                  setMaintenanceEnd('');
                }}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 text-white"
                style={{ background: '#f59e0b' }}
                onClick={activateMaintenance}
                disabled={!maintenanceReason.trim()}
              >
                Activer la maintenance
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Désactiver Maintenance */}
      {showDisableModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div 
            className="w-full max-w-md mx-4 rounded-xl p-6 shadow-xl"
            style={{ background: sidebarBg }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg" style={{ background: '#10b98120' }}>
                <Wrench className="h-6 w-6 text-green-500" />
              </div>
              <h2 className="text-xl font-bold" style={{ color: textPrimary }}>Désactiver la maintenance</h2>
            </div>
            
            <p className="mb-6" style={{ color: textSecondary }}>
              Êtes-vous sûr de vouloir désactiver le mode maintenance ? 
              Les clients et gestionnaires pourront à nouveau accéder à la plateforme.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDisableModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={deactivateMaintenance}
              >
                Désactiver
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
